import { Edit } from '@mui/icons-material'
import {
  IconButton,
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  TextField,
  Button,
} from '@mui/material'
import React from 'react'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { useFormExtended } from '../../../utils/hooks/useFormExtended'
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input'
import { Controller } from 'react-hook-form'
import { getReadableSpecies } from '../../../domain/Animal/AnimalDisplay'
import { getBestCustomerTypeLabel, getReadableCustomerType } from '../../../domain/Customer/CustomerDisplay'
import { CustomerDto, AnimalDtoSpecies, CustomerDtoTypes } from '../../../interactors/gen/backendClient'
import { ControlledMultipleAutoComplete } from '../../common/ControlledMultipleAutoComplete'
import { spacingItem } from '../../theme'
import { MemberCardSaveButton } from '../../Members/MemberCardSaveButton'

interface Props {
  association: CustomerDto
  onSubmit: (data: FormParams) => Promise<void>
  variantButton?: boolean
}

interface FormParams {
  types: CustomerDto['types']
  name: CustomerDto['name']
  SIRETNumber: CustomerDto['SIRETNumber']
  RNANumber: CustomerDto['RNANumber']
  legalForm: CustomerDto['legalForm']
  address: CustomerDto['address']
  streetNumber: CustomerDto['streetNumber']
  postalCode: CustomerDto['postalCode']
  city: CustomerDto['city']
  email: CustomerDto['email']
  phoneNumber: CustomerDto['phoneNumber']
  species: AnimalDtoSpecies[]
  howManyAnimalsPerYear: CustomerDto['howManyAnimalsPerYear']
  howManyAdoptionsPerYear: CustomerDto['howManyAdoptionsPerYear']
}

export const EditTheAssociationPrompt: React.FC<Props> = ({ association, onSubmit, variantButton = false }) => {
  const dialogOpenedState = useDialogOpened()

  const processSubmit = (data: FormParams) => {
    onSubmit(data)
    return dialogOpenedState.closeDialog()
  }

  const getDefaultValues = (customer: CustomerDto): FormParams => ({
    types: customer?.types,
    name: customer?.name,
    SIRETNumber: customer?.SIRETNumber,
    RNANumber: customer?.RNANumber,
    legalForm: customer?.legalForm,
    address: customer?.address,
    streetNumber: customer?.streetNumber,
    postalCode: customer?.postalCode,
    city: customer?.city,
    email: customer?.email,
    phoneNumber: customer?.phoneNumber,
    species: customer?.preferences?.species,
    howManyAnimalsPerYear: customer?.howManyAnimalsPerYear,
    howManyAdoptionsPerYear: customer?.howManyAdoptionsPerYear,
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    register,
  } = useFormExtended(association, getDefaultValues)

  const watchSiret = watch('SIRETNumber')
  const watchRna = watch('RNANumber')

  const isSiretOrRnaFilled = (): boolean => {
    if (watchSiret || watchRna) {
      return true
    }
    return false
  }

  const isCustomerFilledAppropriately = (customer: CustomerDto | null): boolean => {
    if (!customer) return false
    return !!(
      customer.name &&
      customer.email &&
      customer.phoneNumber &&
      customer.address &&
      customer.postalCode &&
      customer.city &&
      customer.organizationType?.type &&
      customer.preferences?.species &&
      customer.preferences.species.length > 0 &&
      (customer.SIRETNumber || customer.RNANumber)
    )
  }

  return (
    <>
      {variantButton ? (
        <Button
          variant="contained"
          color={isCustomerFilledAppropriately(association) ? 'success' : 'primary'}
          sx={{ textTransform: 'none', fontWeight: 700 }}
          onClick={dialogOpenedState.openDialog}
        >
          {isCustomerFilledAppropriately(association) ? `Parfait !` : `À compléter`}
        </Button>
      ) : (
        <IconButton color="primary" onClick={dialogOpenedState.openDialog}>
          <Tooltip title={`Editer ${getBestCustomerTypeLabel(association?.types, { the: true })}`}>
            <Edit />
          </Tooltip>
        </IconButton>
      )}

      <Dialog
        maxWidth="sm"
        open={dialogOpenedState.isDialogOpen}
        onClose={dialogOpenedState.closeDialog}
        fullWidth
        scroll="paper"
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
      >
        <DialogTitle>{`Editer ${getBestCustomerTypeLabel(association?.types, { the: true })}`}</DialogTitle>

        <form
          onSubmit={handleSubmit(processSubmit)}
          style={{ height: '100%', maxHeight: '100%', boxSizing: 'content-box' }}
        >
          <Box
            sx={{
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'hidden',
              boxSizing: 'content-box',
            }}
          >
            <DialogContent sx={{ overflowY: 'scroll', maxHeight: 'calc(100vh - 230px)' }}>
              <Box display="flex" width="100%" alignItems="center">
                <ControlledMultipleAutoComplete
                  control={control}
                  fieldName="types"
                  label="Type d'organisation"
                  error={errors.name}
                  options={Object.values(CustomerDtoTypes).map((type: CustomerDtoTypes) => ({
                    label: getReadableCustomerType(type),
                    value: type,
                  }))}
                  requiredRule=""
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Nom de l'association"
                  type="text"
                  required
                  fullWidth
                  {...register('name', { required: 'Le nom est requis' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Numéro de siret"
                  type="text"
                  required={!isSiretOrRnaFilled()}
                  fullWidth
                  {...register('SIRETNumber', {
                    required: !isSiretOrRnaFilled() ? 'Veuillez remplir le numéro SIRET ou le numéro RNA' : '',
                  })}
                  error={!!errors.SIRETNumber}
                  helperText={errors.SIRETNumber?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Numéro de RNA"
                  type="text"
                  required={!isSiretOrRnaFilled()}
                  fullWidth
                  {...register('RNANumber')}
                  error={!!errors.RNANumber}
                  helperText={errors.RNANumber?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Forme juridique"
                  type="text"
                  fullWidth
                  {...register('legalForm')}
                  error={!!errors.legalForm}
                  helperText={errors.legalForm?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Numéro de rue"
                  type="text"
                  fullWidth
                  {...register('streetNumber')}
                  error={!!errors.streetNumber}
                  helperText={errors.streetNumber?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Adresse de domiciliation"
                  type="text"
                  required
                  fullWidth
                  {...register('address', { required: "L'adresse est requise" })}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Code postal"
                  type="text"
                  required
                  fullWidth
                  {...register('postalCode', { required: 'Le code postal est requis' })}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Ville"
                  type="text"
                  required
                  fullWidth
                  {...register('city', { required: 'La ville est requise' })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  fullWidth
                  {...register('email', {
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: "L'addresse n'est pas valide.",
                    },
                  })}
                  // error={!!errors.email}
                  // helperText={errors.email?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <FormControl fullWidth variant="outlined">
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{
                      validate: (value: string | undefined) => {
                        console.log(value)
                        return !value || value.trim() === '' || matchIsValidTel(value) || value.trim() === '+33'
                          ? true
                          : 'Tel is invalid'
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <MuiTelInput
                        {...field}
                        label="Numero de Téléphone"
                        defaultCountry="FR"
                        helperText={fieldState.error ? "Le numero de téléphone n'est pas valide" : ''}
                        error={!!fieldState.error}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <ControlledMultipleAutoComplete
                  control={control}
                  fieldName="species"
                  label="Espèces"
                  error={errors.name}
                  options={Object.values(AnimalDtoSpecies).map((status: AnimalDtoSpecies) => ({
                    label: getReadableSpecies(status, { withEmoji: false }),
                    value: status,
                  }))}
                  requiredRule=""
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Combien d'animaux sont pris en charge chaque année ?"
                  type="text"
                  fullWidth
                  {...register('howManyAnimalsPerYear')}
                  error={!!errors.howManyAnimalsPerYear}
                  helperText={errors.howManyAnimalsPerYear?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" width="100%" alignItems="center" sx={spacingItem}>
                <TextField
                  label="Sur le nombre de prise en charge, combien sont adoptés chaque année ?"
                  type="text"
                  fullWidth
                  {...register('howManyAdoptionsPerYear')}
                  error={!!errors.howManyAdoptionsPerYear}
                  helperText={errors.howManyAdoptionsPerYear?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <MemberCardSaveButton isOwnAccount={false} />
            </DialogActions>
          </Box>
        </form>
      </Dialog>
    </>
  )
}
